import React from "react";
import styled from "styled-components";
import { graphql } from "gatsby";
import { Router } from "@reach/router";
import { QueryClientProvider, QueryClient } from "react-query";
import Layout from "../../containers";
import { BookingContextProvider } from "../../contexts";
import { mapEdgesToNodes } from "../../lib";

import {
  BookingApp,
  GraphQLErrorList,
  SEO,
  CheckAvailability,
  Profile,
  PrivateRoute,
  Login,
  Details,
  ExistingBookings,
  Payment,
  Confirmed,
  Pending,
  CabinPage,
} from "../../components";
import { routes } from "../../constants";

import { groupByProperty } from "../../lib/helpers";

const queryClient = new QueryClient();

export const query = graphql`
  query BookingPageQuery {
    page: sanityBookings(_id: { regex: "/(drafts.|)bookings/" }) {
      title
      earliestBookingDate
      showCabinPages
      _rawBody
    }
    packages: allSanityStayPackage(
      filter: { isActive: { eq: true } }
      sort: { fields: [numberOfNights], order: ASC }
    ) {
      edges {
        node {
          _id
          title
          description
          numberOfNights
          bookingDays
          peakPrice
          offPeakPrice
        }
      }
    }
    cabins: allSanityCabin(sort: { fields: [title], order: ASC }) {
      edges {
        node {
          _id
          title
          allowDogs
          availableToBook
          _rawBody
        }
      }
    }
    offPeak: allSanityOffPeakPricing {
      edges {
        node {
          title
          dates {
            from
            to
          }
        }
      }
    }
    fixedCosts: allSanityFixedCost(filter: { isActive: { eq: true } }) {
      edges {
        node {
          title
          description
          price
          perItem
        }
      }
    }
  }
`;

const guestLimits = {
  adults: { min: 1, max: 4 },
  children: { min: 0, max: 2 },
  dogs: { min: 0, max: 3 },
};

const App = ({ data, errors }) => {
  if (errors) {
    return (
      <Layout>
        <GraphQLErrorList errors={errors} />
      </Layout>
    );
  }

  const { page, cabins, packages, offPeak, fixedCosts } = data;

  return (
    <Layout>
      <SEO title={page?.title || ""} />
      <Container>
        <QueryClientProvider client={queryClient}>
          <BookingContextProvider
            cabins={mapEdgesToNodes(cabins)}
            packages={mapEdgesToNodes(packages)}
            offPeakData={mapEdgesToNodes(offPeak)}
            fixedCosts={groupByProperty(mapEdgesToNodes(fixedCosts), "perItem")}
            guestLimits={guestLimits}
            earliestBookingDate={page.earliestBookingDate}
          >
            <Router basepath={routes.BASE}>
              <PrivateRoute path={`${routes.PROFILE}`} component={Profile} />
              <PrivateRoute
                path={`${routes.EXISTING_BOOKINGS}`}
                component={ExistingBookings}
              />
              <Login path={`${routes.LOGIN}`} />
              <CheckAvailability path={`${routes.CHECK_AVAILABILITY}`} />
              <Details path={`${routes.BOOKING_DETAILS}`} />
              <Payment path={`${routes.PAYMENT}`} />
              <Pending path={`${routes.PENDING}`} />
              <Confirmed path={`${routes.CONFIRMED}`} />
              <CabinPage path={`${routes.CABINS}/:cabinId`} />
              <BookingApp
                path={`${routes.APP}`}
                body={page?._rawBody || []}
                showCabinPages={page?.showCabinPages}
              />
            </Router>
          </BookingContextProvider>
        </QueryClientProvider>
      </Container>
    </Layout>
  );
};

const Container = styled.div`
  padding: 0 1.5em;
`;

export default App;
